<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import { message } from 'ant-design-vue';
import { useRouter } from 'vue-router';
import ProgressFrame from '../layout/ProgressFrame.vue';
import HorizontalContentRow from '../../components/HorizontalContentRow.vue';
import ProjectForm from '../../components/ProjectForm.vue';
import ButtonPrimary from '@/shared/components/button/ButtonPrimary.vue';
import ChevronRightSVG from '@shared/assets/icon/chevron-right.svg';
import ButtonText from '@/shared/components/button/ButtonText.vue';
import { useProjectStore } from '../../stores/projectStore';
import { ProjectFormModel } from '../../models/Project';

const router = useRouter();
const projectStore = useProjectStore();
const project = ref<ProjectFormModel>(new ProjectFormModel());
const projectFormRef = ref<InstanceType<typeof ProjectForm> | null>(null);

onMounted(() => {
  project.value = projectStore.newProject;
});

const __navigateToWarrantyContact = () => {
  if (!projectStore.newProject.id) {
    throw new Error('Project ID is required to navigate to the warranty contact page');
  }
  router.push({
    name: 'ProjectWarrantyContact',
    params: { projectId: projectStore.newProject.id },
  });
};

const goToNext = async () => {
  /**
   * If the project was already saved before, skip saving it again (as new project)
   * This is to prevent the user from creating multiple projects with the same name.
   */
  if (!projectStore.allowedToCreateNewProject) {
    console.log('Project was already saved before so skipping save');
    __navigateToWarrantyContact();
    return;
  }

  if (!projectFormRef.value?.validateInput()) {
    // Do not proceed if there are errors
    return;
  }

  try {
    await projectStore.createProject(project.value);
    __navigateToWarrantyContact();
  } catch (error: unknown) {
    message.error((error as Error).message);
    return;
  }
};

const goToEditProject = () => {
  const projectId = projectStore.newProject.id;
  if (!projectId) {
    throw new Error('Project ID is required to navigate to the edit project page');
  }
  router.push({ name: 'ProjectEdit', params: { projectId } });
  projectStore.resetNewProject();
};

const disabled = computed(() => !projectStore.allowedToCreateNewProject);
</script>

<template>
  <ProgressFrame pageClass="subsection-page" withBackArrow>
    <template #title>Create project</template>
    <div class="content">
      <HorizontalContentRow firstRow lastRow>
        <ProjectForm
          ref="projectFormRef"
          :project="project"
          :showProjectName="true"
          :disabled="disabled"
          :showPersistedSites="true"
        />
      </HorizontalContentRow>
      <div
        :class="[
          'end-of-page',
          { float: disabled && project.sites.length > 1 },
          { center: disabled && project.sites.length === 1 },
        ]"
      >
        <ButtonPrimary block @click="goToNext" class="next-button" data-tn="next-btn">
          Next
          <template #iconRight>
            <ChevronRightSVG class="chevron-right-icon" />
          </template>
        </ButtonPrimary>

        <template v-if="disabled">
          <span class="edit-project__text" data-tn="edit-project-text">
            Want to edit this project?
          </span>

          <ButtonText
            class="edit-project__button"
            block
            @click="goToEditProject"
            data-tn="edit-project-btn"
          >
            <span class="edit-project__button__text">View or edit project</span>
          </ButtonText>
        </template>
      </div>
    </div>
  </ProgressFrame>
</template>

<style lang="less" scoped>
.content {
  margin: 0;
  padding: 0;
  .content-row {
    width: 100%;
  }
}

.end-of-page {
  padding: @content-side-margin;
  width: 100%;

  .edit-project__text {
    display: block;
    color: @color-primary;
    font-weight: @font-weight-regular;
    text-align: center;
  }

  .next-button,
  .edit-project__button {
    margin-bottom: @margin-bottom-button;
  }

  .edit-project__button__text {
    text-decoration: underline;
    font-weight: @font-weight-regular;
    color: @color-primary;
  }
}

.end-of-page.center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 0;
}

.end-of-page.float {
  position: fixed;
  bottom: 0;
  z-index: 1;
  background-color: @color-background-light;
  border-top: 1px solid @color-mid-tone;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  text-align: center;
}
</style>
